
import React, { useState, useEffect } from 'react';
import { PopupContent } from '@/types/popup-system';

interface LiminalHorrorPopupProps {
  content: PopupContent;
  onDismiss: () => void;
  onAction?: (action: string) => void;
}

export const LiminalHorrorPopup = ({ content, onDismiss, onAction }: LiminalHorrorPopupProps) => {
  const [fluorescent_flicker, setFluorescentFlicker] = useState(false);
  const [drift, setDrift] = useState(false);

  useEffect(() => {
    const flickerTimer = setInterval(() => {
      if (Math.random() < 0.4) {
        setFluorescentFlicker(true);
        setTimeout(() => setFluorescentFlicker(false), 100);
      }
    }, 2000);

    const driftTimer = setInterval(() => {
      setDrift(true);
      setTimeout(() => setDrift(false), 3000);
    }, 8000);

    return () => {
      clearInterval(flickerTimer);
      clearInterval(driftTimer);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-yellow-50/80">
      <div 
        className={`bg-yellow-50 border border-yellow-600 max-w-md w-full mx-4 shadow-lg transition-all duration-1000 ${
          drift ? 'transform translate-x-1 translate-y-0.5' : ''
        } ${fluorescent_flicker ? 'brightness-110' : 'brightness-100'}`}
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1), inset 0 0 50px rgba(255, 255, 224, 0.3)',
          filter: fluorescent_flicker ? 'brightness(1.2) contrast(1.1)' : 'brightness(1)'
        }}
      >
        {/* Fluorescent light effect */}
        <div 
          className={`absolute inset-0 bg-gradient-to-b from-yellow-100/30 to-transparent pointer-events-none ${
            fluorescent_flicker ? 'opacity-80' : 'opacity-40'
          }`}
        />

        <div className="relative z-10">
          <div className="bg-yellow-200 text-yellow-900 px-3 py-2 flex justify-between items-center">
            <span className="text-sm font-medium tracking-wide">
              {content.title}
            </span>
            {content.dismissible && (
              <button 
                onClick={onDismiss}
                className="hover:bg-yellow-300 px-2 py-0.5 text-xs rounded"
              >
                ⊗
              </button>
            )}
          </div>
          
          <div className="p-4 text-yellow-900 bg-yellow-50">
            <div className="border border-yellow-300 p-4 bg-yellow-25 shadow-inner">
              <p className="leading-relaxed whitespace-pre-wrap font-light tracking-wide">
                {content.message}
              </p>
            </div>
            
            {content.actionButtons && content.actionButtons.length > 0 && (
              <div className="mt-4 flex gap-2">
                {content.actionButtons.map((button, index) => (
                  <button
                    key={index}
                    onClick={() => onAction?.(button.action)}
                    className="border border-yellow-400 bg-yellow-100 text-yellow-800 px-4 py-2 hover:bg-yellow-200 transition-colors text-sm shadow-sm"
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
